'use client';

import { useCallback, useState } from 'react';
import type { TutorBundle } from '@peach/types/tutors/tutor-profile.dto.js';
import type { AuthSource } from '@/lib/analytics/auth';
import { useClock } from '@/lib/hooks/useClock';
import { callNoun } from '@/lib/utils/bookable-product';
import { callWhen } from '@/lib/utils/when';
import { SLOT_GONE } from './policies';
import { ProductCard, type PickedSlot } from './ProductCard';
import { SlotPicker } from './SlotPicker';

interface BookSlotCardProps {
  bundle: TutorBundle;
  tutorUsername: string;
  loggedIn: boolean;
  isOwner: boolean;
  firstName: string;
  authSource: AuthSource;
  /** The quiz result is its own colour; the calls card is the plain card ground. */
  surface?: 'accent' | 'card';
  /** The page clock, for naming the picked hour in a signed-out visitor's gate. */
  serverNow: number;
  /** Selling a teammate's product on someone else's storefront: the alias of
      the referral edge it is sold under. */
  squadAlias?: string | null;
}

/**
 * Pick the time, then pay for it: the calendar comes before the price, and the
 * price button is the only button — a tap in the time rail is the pick, and
 * the card it opens below is what asks for the money.
 */
export function BookSlotCard({
  bundle,
  tutorUsername,
  loggedIn,
  isOwner,
  firstName,
  authSource,
  surface = 'accent',
  serverNow,
  squadAlias,
}: BookSlotCardProps) {
  const now = useClock(serverNow);
  const [slot, setSlot] = useState<PickedSlot | null>(null);
  const [taken, setTaken] = useState(false);
  const [reload, setReload] = useState(0);

  const pick = useCallback(async (isoTime: string, timezone: string) => {
    setTaken(false);
    setSlot({ start: isoTime, timezone });
  }, []);

  // The pick is void, so the card goes with it and the rail is refetched: what
  // stays on screen is the sentence and the times that are still real.
  const slotTaken = useCallback(() => {
    setSlot(null);
    setTaken(true);
    setReload((n) => n + 1);
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <SlotPicker
        tutorUsername={tutorUsername}
        bundleId={bundle.id}
        surface={surface}
        reloadToken={reload}
        serverNow={serverNow}
        keep={slot?.start ?? null}
        zoneInTitle
        onPick={pick}
        /* No open times: the pass is still worth having, and its card says
           "waits until {name} opens times" the moment it exists. */
        empty={
          <ProductCard
            bundle={bundle}
            tutorUsername={tutorUsername}
            loggedIn={loggedIn}
            isOwner={isOwner}
            flat
            surface={surface}
            authSource={authSource}
            squadAlias={squadAlias}
            headline={`No times open yet, get the pass and pick one when ${firstName} opens hours`}
          />
        }
      />
      {taken && <span className="text-p-3 font-semibold text-error">{SLOT_GONE}</span>}
      {slot && (
        <ProductCard
          key={slot.start}
          /* The hour is the reason they are signing in, said through `callWhen`
             rather than a second way of dating a call invented here. */
          authPrompt={`Sign in to book ${callWhen(slot.start, now, slot.timezone)} with ${firstName}`}
          bundle={bundle}
          tutorUsername={tutorUsername}
          loggedIn={loggedIn}
          isOwner={isOwner}
          flat
          surface={surface}
          authSource={authSource}
          squadAlias={squadAlias}
          headline={null}
          slot={slot}
          onSlotTaken={slotTaken}
          /* The time is already chosen above, so the verb is Book, not Get. No
             preposition: the price is pushed to the far end of the button, so a
             trailing "for" would hang a word away from the number it governs. */
          actionLabel={
            bundle.numberOfClasses > 1
              ? `Book ${bundle.numberOfClasses} ${callNoun(bundle.numberOfClasses)}`
              : `Book ${bundle.duration} min`
          }
        />
      )}
    </div>
  );
}
